/**
 * Which columns the Explorer's Table view shows, for a given density.
 *
 * This mapping used to exist only as JSX conditions repeated per column, twice
 * (once in the header row, once in the body row), in the shape
 * `density === 'detailed' || (isCustomMode && visibleColumns.aging)`. That was
 * fine while the density was the only thing choosing columns. Ion's
 * `set_table_columns` needs the same answer — specifically, "which columns can
 * the user see right now", so that ADDING a column keeps the ones already there
 * — and deriving it a second time in the command layer would have meant two
 * definitions of the same rule, drifting apart on the next column added.
 *
 * The rule, stated once:
 *   standard  — identity columns plus the headline performance pair
 *   detailed  — those, plus the quality metrics and chemistry
 *   custom    — exactly what the user (or Ion) selected
 */

/** Every toggleable column, in table order. Mirrors `TABLE_COLUMN_KEYS` in
 *  backend/app/api/v1/chat/explorer_vocab.py — the set Ion may name. */
export const TABLE_COLUMNS = [
  'name',
  'manufacturer',
  'format',
  'nomCapacity',
  'energyPower',
  'batchVar',
  'aging',
  'chemistry',
  'voltage',
  'mass',
]

/** Columns each non-custom density shows. `voltage` and `mass` are custom-only
 *  — no density includes them, which is why they are absent from both lists. */
const DENSITY_COLUMNS = {
  standard: ['name', 'manufacturer', 'format', 'nomCapacity', 'energyPower'],
  detailed: [
    'name',
    'manufacturer',
    'format',
    'nomCapacity',
    'energyPower',
    'batchVar',
    'aging',
    'chemistry',
  ],
}

/** The custom-mode starting point, and the default for `tableVisibleColumns`. */
export const DEFAULT_CUSTOM_COLUMNS = Object.fromEntries(
  TABLE_COLUMNS.map((key) => [key, DENSITY_COLUMNS.standard.includes(key)]),
)

/**
 * Flag object for the columns visible at `density`.
 *
 * @param {'standard'|'detailed'|'custom'} density
 * @param {Record<string, boolean>} [customFlags] - `tableVisibleColumns`; used
 *   only when density is 'custom'
 */
export function columnsForDensity(density, customFlags) {
  if (density === 'custom') {
    // Normalised over the full key list, so a stale persisted object missing a
    // key added later reads as `false` rather than `undefined` — the JSX treats
    // them the same, but the command layer's merge does not.
    return Object.fromEntries(TABLE_COLUMNS.map((key) => [key, !!customFlags?.[key]]))
  }
  const shown = DENSITY_COLUMNS[density] || DENSITY_COLUMNS.standard
  return Object.fromEntries(TABLE_COLUMNS.map((key) => [key, shown.includes(key)]))
}
